/**
 * Editor для ref.nomenclature_retail_prices (Порція Роздріб.5b).
 * Прив'язка ціни до номенклатура+ціновая група. Без posted.
 */
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { nomenclature as nomApi, orgs } from '@/api/catalogs';
import {
  retailNomenclaturePrices,
} from '@/api/retailNomenclaturePrices';
import { retailPriceGroups } from '@/api/retailPriceGroups';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import { validateRequired } from '@/utils/validateRequired';
import { extractApiError } from '@/utils/extractApiError';
import RequirePermission from '@/auth/RequirePermission';
import RequiredLabel from '@/components/RequiredLabel';
import DatePopover from '@/components/DatePopover';

const PERMISSION = 'retail.nomenclature_prices.manage';

interface FormState {
  organization_id: string;
  nomenclature_id: string;
  price_group_id: string;
  price: string;
  vat_included: boolean;
  valid_from: string;
  valid_to: string;
  is_active: boolean;
  comment: string;
}

const EMPTY: FormState = {
  organization_id: '',
  nomenclature_id: '',
  price_group_id: '',
  price: '0',
  vat_included: true,
  valid_from: '',
  valid_to: '',
  is_active: true,
  comment: '',
};

export default function NomenclatureRetailPriceEditor() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { id } = useParams();
  const isNew = !id || id === 'new';

  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const { availableOrgs } = usePermissionsByOrg(PERMISSION);
  const nomQ = useQuery({ queryKey: ['nomenclature-full'], queryFn: () => nomApi.list({ limit: 500 }) });

  const [form, setForm] = useState<FormState>(EMPTY);
  const [error, setError] = useState<string | null>(null);

  const pgQ = useQuery({
    queryKey: ['retail-price-groups-for-editor', form.organization_id],
    queryFn: () => retailPriceGroups.list({ organization_id: form.organization_id, limit: 500 }),
    enabled: !!form.organization_id,
  });

  const docQuery = useQuery({
    queryKey: ['retail-nomenclature-price', id],
    queryFn: () => retailNomenclaturePrices.get(id!),
    enabled: !isNew,
  });

  useEffect(() => {
    if (isNew && availableOrgs.length > 0 && !form.organization_id) {
      setForm((f) => ({ ...f, organization_id: availableOrgs[0]!.id }));
    }
  }, [isNew, availableOrgs, form.organization_id]);

  useEffect(() => {
    if (!isNew && docQuery.data) {
      const d = docQuery.data;
      setForm({
        organization_id: d.organization_id,
        nomenclature_id: d.nomenclature_id,
        price_group_id: d.price_group_id,
        price: d.price,
        vat_included: d.vat_included,
        valid_from: d.valid_from?.slice(0, 16) ?? '',
        valid_to: d.valid_to?.slice(0, 16) ?? '',
        is_active: d.is_active,
        comment: d.comment ?? '',
      });
    }
  }, [isNew, docQuery.data]);

  const toIso = (v: string) => (v ? new Date(v).toISOString() : null);

  const create = useMutation({
    mutationFn: () =>
      retailNomenclaturePrices.create({
        organization_id: form.organization_id,
        nomenclature_id: form.nomenclature_id,
        price_group_id: form.price_group_id,
        price: form.price,
        vat_included: form.vat_included,
        valid_from: toIso(form.valid_from),
        valid_to: toIso(form.valid_to),
        is_active: form.is_active,
        comment: form.comment || null,
      }),
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['retail-nomenclature-prices'] });
      navigate(`/retail/nomenclature-retail-prices/${doc.id}`);
    },
    onError: (e) => setError(extractApiError(e)),
  });

  const update = useMutation({
    mutationFn: () =>
      retailNomenclaturePrices.update(id!, {
        price: form.price,
        vat_included: form.vat_included,
        valid_from: toIso(form.valid_from),
        valid_to: toIso(form.valid_to),
        is_active: form.is_active,
        comment: form.comment || null,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['retail-nomenclature-prices'] });
      qc.invalidateQueries({ queryKey: ['retail-nomenclature-price', id] });
    },
    onError: (e) => setError(extractApiError(e)),
  });

  const remove = useMutation({
    mutationFn: () => retailNomenclaturePrices.remove(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['retail-nomenclature-prices'] });
      navigate('/retail/nomenclature-retail-prices');
    },
    onError: (e) => setError(extractApiError(e)),
  });

  const handleSave = () => {
    setError(null);
    const err = validateRequired(
      {
        organization_id: form.organization_id,
        nomenclature_id: form.nomenclature_id,
        price_group_id: form.price_group_id,
        price: form.price,
      },
      {
        organization_id: 'Юрособа',
        nomenclature_id: 'Номенклатура',
        price_group_id: 'Цінова група',
        price: 'Ціна',
      },
    );
    if (err) {
      setError(err);
      return;
    }
    if (isNew) create.mutate();
    else update.mutate();
  };

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>
          {isNew ? 'Нова роздрібна ціна' : 'Роздрібна ціна'}
        </h2>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button onClick={() => navigate('/retail/nomenclature-retail-prices')}>
            Скасувати
          </button>
          <RequirePermission code={PERMISSION}>
            <button
              className="primary"
              onClick={handleSave}
              disabled={create.isPending || update.isPending}
            >
              Зберегти
            </button>
          </RequirePermission>
          {!isNew && (
            <RequirePermission code={PERMISSION}>
              <button
                className="danger"
                onClick={() => {
                  if (window.confirm('Видалити ціну?')) remove.mutate();
                }}
              >
                Видалити
              </button>
            </RequirePermission>
          )}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
        <div className="field">
          <RequiredLabel>Організація</RequiredLabel>
          {isNew ? (
            <select
              value={form.organization_id}
              onChange={(e) => setForm({ ...form, organization_id: e.target.value })}
            >
              <option value="">—</option>
              {availableOrgs.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.code} — {o.name}
                </option>
              ))}
            </select>
          ) : (
            <div>
              {orgList.data?.items.find((o) => o.id === form.organization_id)?.name ??
                form.organization_id}
            </div>
          )}
        </div>
        <div className="field">
          <RequiredLabel>Номенклатура</RequiredLabel>
          <select
            value={form.nomenclature_id}
            onChange={(e) => setForm({ ...form, nomenclature_id: e.target.value })}
            disabled={!isNew}
          >
            <option value="">—</option>
            {nomQ.data?.items.map((n) => (
              <option key={n.id} value={n.id}>
                {n.code} — {n.name}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <RequiredLabel>Цінова група</RequiredLabel>
          <select
            value={form.price_group_id}
            onChange={(e) => setForm({ ...form, price_group_id: e.target.value })}
            disabled={!isNew}
          >
            <option value="">—</option>
            {pgQ.data?.items.map((g) => (
              <option key={g.id} value={g.id}>
                {g.code} — {g.name}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <RequiredLabel>Ціна</RequiredLabel>
          <input
            type="number"
            step="0.01"
            value={form.price}
            onChange={(e) => setForm({ ...form, price: e.target.value })}
          />
        </div>
        <div className="field">
          <label>Діє з</label>
          <DatePopover
            withTime
            value={form.valid_from}
            onChange={(v) => setForm({ ...form, valid_from: v })}
          />
        </div>
        <div className="field">
          <label>Діє до</label>
          <DatePopover
            withTime
            value={form.valid_to}
            onChange={(v) => setForm({ ...form, valid_to: v })}
          />
        </div>
        <div className="field">
          <label>
            <input
              type="checkbox"
              checked={form.vat_included}
              onChange={(e) => setForm({ ...form, vat_included: e.target.checked })}
            />
            {' '}З ПДВ
          </label>
        </div>
        <div className="field">
          <label>
            <input
              type="checkbox"
              checked={form.is_active}
              onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
            />
            {' '}Активна
          </label>
        </div>
        <div className="field" style={{ gridColumn: 'span 2' }}>
          <label>Коментар</label>
          <textarea
            value={form.comment}
            onChange={(e) => setForm({ ...form, comment: e.target.value })}
            rows={2}
          />
        </div>
      </div>

      {error && (
        <div className="error" style={{ marginTop: '1rem', color: '#b91c1c' }}>
          {error}
        </div>
      )}
    </div>
  );
}
